import { Injectable } from '@angular/core';
import {
  AngleIron,
  AngleIronBox,
  angleIronMesh,
  ViewerAngleIronLayer,
} from '@App/app/entities/layer/angle-iron.model';
import { createGui } from '@App/app/entities/layer/create-gui.model';
import { Coordinates4 } from '@App/app/entities/layer/measurements/coordinates';
import { AbstractMesh, BoundingBox, Color4, CSG, Mesh, Quaternion, Vector3 } from 'babylonjs';
import { ELECTRIC_BLUE_COLOR } from '@App/app/configs/babylon.config';
import { BabylonNodesService } from '../../babylon-nodes-service/babylon-nodes.service';
import { GenerateGUIService } from '../../gui-services/generate-gui-service/generate-gui.service';
import { MaterialService } from '../../material-service/material.service';
import { SceneService } from '../../scene-service/scene.service';

@Injectable({
  providedIn: 'root',
})
export class AngleIronMeshService implements createGui {
  constructor(
    public generateGUIService: GenerateGUIService,
    private babylonNodesService: BabylonNodesService,
    private materialService: MaterialService,
    private sceneService: SceneService,
  ) {}

  showAngleIron(
    angleIron: AngleIron,
    boxes: [AngleIronBox, AngleIronBox],
    absoluteRotationQuaternion: Coordinates4,
  ) {
    const newAngleIron = angleIron as ViewerAngleIronLayer;
    const meshes = this.createBoxes(boxes, absoluteRotationQuaternion);
    newAngleIron.mesh = CSG.FromMesh(meshes[0])
      .union(CSG.FromMesh(meshes[1]))
      .toMesh(
        angleIronMesh.defaultName,
        this.materialService.electricBlueMaterial,
        this.sceneService.scene,
        false,
      );
    newAngleIron.mesh.material = this.materialService.electricBlueMaterial;
    newAngleIron.mesh.renderingGroupId = 0;
    newAngleIron.mesh.visibility = 0.6;
    newAngleIron.mesh.enableEdgesRendering();
    newAngleIron.mesh.edgesWidth = 0.1;
    newAngleIron.mesh.edgesColor = Color4.FromHexString(`${ELECTRIC_BLUE_COLOR}FF`);
    for (let i = 0; i < meshes.length; i++) {
      meshes[i].dispose();
      delete meshes[i];
    }
    newAngleIron.gui = this.createGui(newAngleIron);
    return newAngleIron;
  }

  createGui(layer: ViewerAngleIronLayer) {
    if (layer.mesh) {
      return this.generateGUIService.createNameTag(layer.name, layer.mesh, true, layer.id);
    }
  }

  createBoxes(boxes: [AngleIronBox, AngleIronBox], quaternion: Coordinates4): [Mesh, Mesh] {
    const rotation = new Quaternion(quaternion.x, quaternion.y, quaternion.z, quaternion.w);
    return boxes.map((box) => {
      if (box.min && box.max) {
        const min = new Vector3(box.min.x, box.min.y, box.min.z);
        const max = new Vector3(box.max.x, box.max.y, box.max.z);
        const boundingBox = new BoundingBox(min, max);
        const mesh = this.babylonNodesService.createBox(
          boundingBox.centerWorld,
          {
            width: boundingBox.extendSize.x * 2,
            height: boundingBox.extendSize.y * 2,
            depth: boundingBox.extendSize.z * 2,
          },
          rotation,
        );
        mesh.visibility = 0.6;
        return mesh;
      }
    }) as [Mesh, Mesh];
  }

  changeSampleMaterial(sample: AbstractMesh, defaultMaterial: boolean) {
    sample.material = defaultMaterial
      ? this.materialService.electricBlueMaterial
      : this.materialService.redMaterial;
  }
}
